import React from 'react';
import { Link, graphql } from 'gatsby';
import { MDXRenderer } from 'gatsby-plugin-mdx';
import classnames from 'classnames';
import slugify from '@sindresorhus/slugify';
import { useModal } from 'react-modal-hook';

import {
  useNavigationState,
  useNavigationDispatch,
} from '../contexts/navigation';

import MenuToggle from '../components/MenuToggle';
import FeedbackForm from '../components/FeedbackForm';
import DocsSearch from '../components/DocsSearch';
import Modal from '../components/Modal';
import DocsQuickstart from '../components/DocsQuickstart';

import LogoSVG from '../svg/logo.svg';
import SearchSVG from '../svg/icons/search.svg';
import LoginSVG from '../svg/icons/login.svg';
import GitHubSVG from '../svg/icons/github.svg';
import SlackSVG from '../svg/icons/slack.svg';
import CodeSVG from '../svg/icons/code.svg';
import BookSVG from '../svg/icons/book.svg';
import ThumbUpFillIcon from 'remixicon-react/ThumbUpFillIcon';
import ThumbDownFillIcon from 'remixicon-react/ThumbDownFillIcon';

const groups = {
  'docs/schema': 'Schema',
  'docs/content-api': 'Content API',
  'docs/develop': 'Develop',
  'user-guides/working-with': 'Working with',
};

function TOC({ frontmatter, headings }) {
  if ((headings && !headings.length > 0) || frontmatter?.disableTOC)
    return null;

  return (
    <React.Fragment>
      <p className="py-2 text-primary-300 uppercase tracking-wider font-medium text-sm lg:text-xs">
        On this page
      </p>

      <ul>
        {headings.map(({ value, depth }) => {
          const href = `#${slugify(value, {
            customReplacements: [['/', '']],
            decamelize: false,
          })}`;

          return (
            <li key={value}>
              <a
                href={href}
                className={classnames(
                  'text-gray-700 hover:text-cornflower text-sm block',
                  {
                    'py-1': depth === 2,
                    'pl-2 pt-1': depth === 3,
                    'pl-4 pt-1': depth === 4,
                  }
                )}
              >
                {value}
              </a>
            </li>
          );
        })}
      </ul>
    </React.Fragment>
  );
}

export default function DocPage({
  pageContext: { node },
  data: { navFiles },
  location,
}) {
  const { currentPath, isOpen } = useNavigationState();
  const { toggleNav } = useNavigationDispatch();

  const { childMdx } = node;
  const { frontmatter, body } = childMdx;
  const { title, disableFeedback } = frontmatter;
  const { pathname } = location;
  const [, context] = pathname.split('/');

  const sortedNavFiles = [...navFiles.group].sort(
    (a, b) =>
      a.nodes.find((node) => node.base === 'index.mdx')?.childMdx?.frontmatter
        ?.position -
      b.nodes.find((node) => node.base === 'index.mdx')?.childMdx?.frontmatter
        ?.position
  );

  const searchInputRef = React.useRef(null);

  const [showSearch, hideSearch] = useModal(() => (
    <Modal
      closeHandler={hideSearch}
      titleText="Search"
      initialFocusRef={searchInputRef}
    >
      <DocsSearch ref={searchInputRef} />
    </Modal>
  ));

  const [showFeedbackForm, closeFeedbackForm] = useModal(() => (
    <Modal
      closeHandler={closeFeedbackForm}
      titleText="We want to hear from you"
    >
      <FeedbackForm href={pathname} />
    </Modal>
  ));

  return (
    <div className="min-h-screen">
      <div className="xl:container mx-auto">
        <div className="flex flex-wrap relative px-6 lg:h-screen">
          <aside className="sidebar w-full lg:w-1/5 lg:block lg:relative lg:inset-0 lg:mt-0">
            <div className="py-6 lg:pb-0 lg:pt-8 pl-2 relative z-20 border-b border-primary-150 lg:border-none lg:sticky lg:top-0 lg:overflow-y-auto lg:h-auto lg:max-h-screen">
              <div className="w-full flex justify-between items-center lg:mb-6">
                <Link to="/" className="lg:text-white">
                  <LogoSVG className="h-6 xl:h-8" />
                </Link>

                <div className="ml-auto pr-6 flex items-center justify-center">
                  <button
                    onClick={showSearch}
                    className="focus:outline-none text-white"
                  >
                    <span className="sr-only">Search</span>
                    <SearchSVG className="w-5 h-5" />
                  </button>
                </div>

                <MenuToggle
                  className="text-haiti lg:hidden"
                  onClick={toggleNav}
                  open={isOpen}
                />
              </div>

              <div
                className={classnames('pt-6 lg:pt-0', {
                  'block lg:block': isOpen,
                  'hidden lg:block': !isOpen,
                })}
              >
                <ul className="pb-6 lg:pb-0 lg:pr-5">
                  <li className="-ml-2 mb-1">
                    <Link
                      to="/docs"
                      className={classnames('py-1 px-2 flex items-center', {
                        'bg-primary-150 lg:bg-rose text-cornflower lg:text-white rounded lg:shadow-sm':
                          context === 'docs',
                        'text-gray-500': context !== 'docs',
                      })}
                    >
                      <span className="mr-2">
                        <CodeSVG className="w-5 h-5" />
                      </span>
                      Developer Docs
                    </Link>
                  </li>
                  <li className="-ml-2 mb-1">
                    <Link
                      to="/user-guides"
                      className={classnames('py-1 px-2 flex items-center', {
                        'bg-primary-150 lg:bg-rose text-cornflower lg:text-white rounded lg:shadow-sm':
                          context === 'user-guides',
                        'text-gray-500': context !== 'user-guides',
                      })}
                    >
                      <span className="mr-2">
                        <BookSVG className="w-5 h-5" />
                      </span>
                      User Guides
                    </Link>
                  </li>
                </ul>

                <div className="lg:pr-5">
                  <div className="lg:py-8">
                    {sortedNavFiles.map(({ fieldValue, nodes }, index) => {
                      const isGroup = nodes.length > 1;

                      return (
                        <div
                          key={index}
                          className={classnames(null, { 'pt-6': isGroup })}
                        >
                          {isGroup && (
                            <p className="mb-2 text-light-haiti uppercase tracking-wider font-medium text-sm lg:text-xs">
                              {groups[fieldValue]}
                            </p>
                          )}

                          <ul>
                            {nodes.map(({ base, childMdx, id }) => {
                              if (isGroup && base === 'index.mdx') return null;
                              const isActive =
                                childMdx?.fields?.slug === currentPath;

                              return (
                                <li key={id}>
                                  <Link
                                    to={childMdx?.fields?.slug}
                                    className={classnames(
                                      'py-1 block duration-100 ease-in-out transition-all rounded',
                                      {
                                        'text-rose': isActive,
                                        'text-gray-500 hover:text-rose': !isActive,
                                      }
                                    )}
                                  >
                                    {childMdx?.frontmatter?.nav ||
                                      childMdx?.frontmatter?.title}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </aside>
          <div className="flex flex-wrap w-full lg:w-4/5">
            <div className="lg:bg-white w-full xl:w-3/4 xl:border-r xl:border-primary-150">
              <div className="relative">
                <div className="py-4 lg:py-8 lg:px-8">
                  <main className="max-w-none prose">
                    <h1 className="flex items-center justify-between">
                      {title}
                    </h1>
                    <MDXRenderer>{body}</MDXRenderer>
                  </main>

                  {pathname === '/docs' && <DocsQuickstart />}
                </div>
              </div>
              {!disableFeedback && (
                <div className="lg:p-8">
                  <div className="pt-6 border-t border-primary-200 md:flex md:items-center md:space-x-6">
                    <div className="w-full">
                      <h4 className="text-cornflower text-lg md:text-xl font-medium">
                        Did you find this page useful?
                      </h4>
                      <p className="leading-relaxed text-primary-300">
                        Your feedback helps us improve our docs, and product.
                      </p>
                    </div>
                    <div className="flex items-center">
                      <button
                        onClick={showFeedbackForm}
                        className="appearance-none p-3 flex items-center justify-center text-gray-500 hover:text-cornflower transition duration-75 ease-in-out focus:outline-none"
                      >
                        <ThumbUpFillIcon className="w-6 h-6" />
                      </button>
                      <button
                        onClick={showFeedbackForm}
                        className="appearance-none p-3 flex items-center justify-center text-gray-500 hover:text-cornflower transition duration-75 ease-in-out focus:outline-none"
                      >
                        <ThumbDownFillIcon className="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="hidden xl:block xl:w-1/4 xl:py-4 xl:pt-8 xl:pb-4">
              <div className="flex items-space space-x-2 md:px-6">
                <a
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  href="https://app.graphcms.com"
                  className="text-primary-300 hover:text-cornflower"
                >
                  <LoginSVG className="w-5 h-5" />
                </a>
                <a
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  href="https://github.com/graphcms"
                  className="text-primary-300 hover:text-cornflower"
                >
                  <GitHubSVG className="w-5 h-5" />
                </a>
                <a
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  href="https://slack.graphcms.com"
                  className="text-primary-300 hover:text-cornflower"
                >
                  <SlackSVG className="w-5 h-5" />
                </a>
              </div>

              <div className="lg:px-6 lg:sticky lg:top-0 pt-8">
                <TOC {...childMdx} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const pageQuery = graphql`
  query DocPageQuery($relativeDirectory: String) {
    navFiles: allFile(
      filter: {
        extension: { in: ["md", "mdx"] }
        childMdx: { frontmatter: { hidden: { ne: true } } }
        relativeDirectory: { regex: $relativeDirectory }
      }
      sort: { fields: childMdx___frontmatter___position, order: ASC }
    ) {
      group(field: relativeDirectory) {
        fieldValue
        nodes {
          id
          base
          childMdx {
            fields {
              slug
            }
            frontmatter {
              nav
              title
              position
            }
          }
        }
      }
    }
  }
`;
